import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "@/api";
import { queryKeys } from "@/api/queryKeys";

type QuoteResponse = { offerId: string; kind?: string; quoteCents: number };
type ExecuteResponse = { paidCents?: number };
export type RipDecision = "keep" | "cash";

/** The open window's keep-or-cash action: quote + execute one share and
 * remember what it paid, so reveal cards can flip to their CASHED state.
 * Sales after the window ride the same endpoints at the standing bid. */
export function useInstantCash() {
  const queryClient = useQueryClient();
  const [cashedByShareId, setCashedByShareId] = useState<
    Record<number, number>
  >({});
  const [busyShareId, setBusyShareId] = useState<number | null>(null);
  const [decisionsByShareId, setDecisionsByShareId] = useState<
    Record<number, RipDecision>
  >({});
  const [bulkBusy, setBulkBusy] = useState(false);

  const mutation = useMutation({
    mutationFn: async (shareId: number) => {
      const quote = (await api.buybackQuote(shareId)) as QuoteResponse;
      const executed = (await api.buybackExecute(
        quote.offerId,
      )) as ExecuteResponse;
      return { shareId, paidCents: executed.paidCents ?? quote.quoteCents };
    },
    onMutate: (shareId) => setBusyShareId(shareId),
    onSettled: () => setBusyShareId(null),
    onSuccess: ({ shareId, paidCents }) => {
      setCashedByShareId((current) => ({ ...current, [shareId]: paidCents }));
      setDecisionsByShareId((current) => ({
        ...current,
        [shareId]: "cash",
      }));
      void queryClient.invalidateQueries({ queryKey: ["stable"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.balanceRoot });
    },
  });

  const keepShare = (shareId: number) => {
    if (cashedByShareId[shareId] != null) return;
    setDecisionsByShareId((current) => ({ ...current, [shareId]: "keep" }));
  };

  const cashBatch = async (shareIds: number[]) => {
    if (bulkBusy || shareIds.length === 0) return [];
    setBulkBusy(true);
    try {
      const idempotencyKey = crypto.randomUUID();
      const result = await api.buybackExecuteBatch(shareIds, idempotencyKey);
      const paid = Object.fromEntries(
        result.items.map((item) => [item.shareId, item.paidCents]),
      );
      setCashedByShareId((current) => ({ ...current, ...paid }));
      setDecisionsByShareId((current) => ({
        ...current,
        ...Object.fromEntries(
          result.items.map((item) => [item.shareId, "cash" as const]),
        ),
      }));
      void queryClient.invalidateQueries({ queryKey: ["stable"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.balanceRoot });
      return result.items;
    } finally {
      setBulkBusy(false);
    }
  };

  return {
    cashedByShareId,
    decisionsByShareId,
    busyShareId,
    bulkBusy,
    keepShare,
    cashShare: mutation.mutateAsync,
    cashBatch,
    cashError: mutation.error,
  };
}
